import React, { useEffect, useRef } from 'react';
import styled from 'styled-components';
import { TextStyle } from '@client/style/TextStyles';
import { DateUtils } from '@client/core/utils/DateUtils';
import gsap from 'gsap';
import { ScrollToPlugin } from 'gsap/all';
import { useDateRange } from './utils/SpilleplanHooks';
import useIsMobile from '@client/core/CustomHooks/useIsMobile';

const Dates = styled.div`
	display: flex;
	position: relative;
	padding-bottom: 40px;
	justify-content: start;
	width: fit-content;
	position: sticky;
	left: 0;
`;

const DateHolder = styled.button<{ $active?: boolean }>`
	${TextStyle.family('title')}
	${TextStyle.weight('bold')}
	text-align: left;
	color: inherit;
	padding: 0 calc(var(--gridGutter) * 0.5);
	padding-left: var(--gridMargin);
	margin-right: calc((var(--gridMargin) - (var(--gridGutter) * 0.5)) * -1);
	position: relative;

	${props =>
		props.$active
			? `
		opacity: 1;
		z-index: 1;
		`
			: `
			z-index: 2;
		cursor: pointer;
		opacity: 0.1;
		transition: opacity .1s;

		&:hover {
			opacity: 0.15;
		}
		`};
`;

const DateName = styled.p`
	${TextStyle.size('p1', 'h5', 'laptop')}
	line-height: 1.1em;
`;

const DateNumber = styled.p`
	${TextStyle.size('p6')}
	line-height: 1.1em;
`;

export default function DateButtonList({
	events,
	active,
	setActive,
}: {
	events: any[];
	active: number;
	setActive: (index: number) => void;
}) {
	const datesRef = useRef<HTMLDivElement>(null);
	const dates = useDateRange(events);
	const isMobile = useIsMobile();

	// update date list offset
	useEffect(() => {
		if (!datesRef.current) return;
		const targets = Array.from(datesRef.current.querySelectorAll<HTMLElement>('button'));
		const target = targets[active];

		if (target) {
			gsap.to(datesRef.current, {
				duration: 0.5,
				left: () => targets[active].offsetLeft * -1,
				ease: 'back(1.3).out',
			});
		}
	}, [active, datesRef.current, isMobile]);

	return (
		<Dates ref={datesRef}>
			{dates.map((date, index) => (
				<DateHolder
					key={date.getTime()}
					$active={index === active}
					className={`${index === active ? 'active' : ''}`}
					onClick={() => {
						setActive(index);
					}}
				>
					<DateName>{DateUtils.getWeekDayName(date)}</DateName>
					<DateNumber>{DateUtils.getDanishDateMonthYearString(date, false)}</DateNumber>
				</DateHolder>
			))}
		</Dates>
	);
}
